import "@/App.css";
import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { googleSession } from "@/lib/api";
import { ClientProvider } from "@/context/ClientContext";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/layout/AppLayout";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AgencyDashboardPage from "@/pages/AgencyDashboardPage";
import ExecutiveDashboard from "@/pages/ExecutiveDashboard";
import OwnerDashboardPage from "@/pages/OwnerDashboardPage";
import ExecutiveHome from "@/pages/ExecutiveHome";
import DashboardGrid from "@/pages/DashboardGrid";
import Dashboard from "@/pages/Dashboard";
import GA4Page from "@/pages/GA4Page";
import AdsPage from "@/pages/AdsPage";
import ApprovalQueue from "@/pages/ApprovalQueue";
import AiManagerPage from "@/pages/AiManagerPage";
import AiStudioPage from "@/pages/AiStudioPage";
import AiImageStudioPage from "@/pages/AiImageStudioPage";
import BrandManagerPage from "@/pages/BrandManagerPage";
import MarketingDirectorPage from "@/pages/MarketingDirectorPage";
import MarketingCalendarPage from "@/pages/MarketingCalendarPage";
import MonthlyPlannerPage from "@/pages/MonthlyPlannerPage";
import FeaturesPage from "@/pages/marketing/FeaturesPage";
import AiTeamPage from "@/pages/marketing/AiTeamPage";
import IndustriesPage from "@/pages/marketing/IndustriesPage";
import PublicPricingPage from "@/pages/marketing/PublicPricingPage";
import StoriesPage from "@/pages/marketing/StoriesPage";
import FaqPage from "@/pages/marketing/FaqPage";
import BlogPage from "@/pages/marketing/BlogPage";
import BlogPostPage from "@/pages/marketing/BlogPostPage";
import ContactPage from "@/pages/marketing/ContactPage";
import PlanPage from "@/pages/PlanPage";
import ChecklistPage from "@/pages/ChecklistPage";
import CalendarPage from "@/pages/CalendarPage";
import LibraryPage from "@/pages/LibraryPage";
import EmailPage from "@/pages/EmailPage";
import SeoPage from "@/pages/SeoPage";
import PromotionsPage from "@/pages/PromotionsPage";
import ConversionOptimizer from "@/pages/ConversionOptimizer";
import CompetitorsPage from "@/pages/CompetitorsPage";
import AdBudgetPage from "@/pages/AdBudgetPage";
import RoasPage from "@/pages/RoasPage";
import ProductsPage from "@/pages/ProductsPage";
import CustomersPage from "@/pages/CustomersPage";
import ReportsPage from "@/pages/ReportsPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import ShopifyPage from "@/pages/ShopifyPage";
import ProductIntelligencePage from "@/pages/ProductIntelligencePage";
import LaunchChecklistPage from "@/pages/LaunchChecklistPage";
import ConnectionsHealthPage from "@/pages/ConnectionsHealthPage";
import SystemReadinessPage from "@/pages/SystemReadinessPage";
import DeploymentValidationPage from "@/pages/DeploymentValidationPage";
import ProfilePage from "@/pages/ProfilePage";
import AiSocialManagerPage from "@/pages/AiSocialManagerPage";
import AiVideoStudioPage from "@/pages/AiVideoStudioPage";
import CampaignHistoryPage from "@/pages/CampaignHistoryPage";
import AiCommsManagerPage from "@/pages/AiCommsManagerPage";
import AiRevenueManagerPage from "@/pages/AiRevenueManagerPage";
import AiEmployeesPage from "@/pages/AiEmployeesPage";
import AiSpecialistPage from "@/pages/AiSpecialistPage";
import BrandAssetsPage from "@/pages/BrandAssetsPage";
import TemplatesPage from "@/pages/TemplatesPage";
import AuditLogsPage from "@/pages/AuditLogsPage";
import BillingPage from "@/pages/BillingPage";
import BillingUsagePage from "@/pages/BillingUsagePage";
import UsageControlPage from "@/pages/UsageControlPage";
import LinkedInCenterPage from "@/pages/LinkedInCenterPage";
import MediaLibraryPage from "@/pages/MediaLibraryPage";
import ContentStrategyPage from "@/pages/ContentStrategyPage";
import LeadsPage from "@/pages/LeadsPage";
import BusinessAuditPage from "@/pages/BusinessAuditPage";
import OnboardingPage from "@/pages/OnboardingPage";
import LandingPage from "@/pages/LandingPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import DataDeletionPage from "@/pages/DataDeletionPage";
import DrezoroLayout from "@/pages/drezoro/DrezoroLayout";
import DrezoroHome from "@/pages/drezoro/DrezoroHome";
import DrezoroAbout from "@/pages/drezoro/DrezoroAbout";
import DrezoroCompanies from "@/pages/drezoro/DrezoroCompanies";
import DrezoroTechnology from "@/pages/drezoro/DrezoroTechnology";
import DrezoroNews from "@/pages/drezoro/DrezoroNews";
import DrezoroContact from "@/pages/drezoro/DrezoroContact";
import DrezoroLegal from "@/pages/drezoro/DrezoroLegal";

const dz = (page) => <DrezoroLayout>{page}</DrezoroLayout>;

function AuthGate() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D14] flex items-center justify-center text-[#9a9a9a] ai-thinking" data-testid="auth-loading">
        Loading Vozelto…
      </div>
    );
  }
  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  const isAdmin = user.role === "super_admin" || user.role === "agency_admin";
  const seesAll = user.role === "super_admin" || user.role === "agency_admin" || user.role === "agency_staff";

  return (
    <ClientProvider>
      <AppLayout>
        <ErrorBoundary key={location.pathname}>
        <Routes>
          {seesAll && <Route path="/agency" element={<AgencyDashboardPage />} />}
          {seesAll && <Route path="/audit" element={<AuditLogsPage />} />}
          {seesAll && <Route path="/owner" element={<OwnerDashboardPage />} />}
          <Route path="/" element={<ExecutiveDashboard />} />
          <Route path="/home" element={<ExecutiveHome />} />
          <Route path="/dashboard" element={<DashboardGrid />} />
          <Route path="/analytics" element={<Dashboard />} />
          <Route path="/ga4" element={<GA4Page />} />
          <Route path="/ads" element={<AdsPage />} />
          <Route path="/approvals" element={<ApprovalQueue />} />
          <Route path="/ai-manager" element={<AiManagerPage />} />
          <Route path="/ai-studio" element={<AiStudioPage />} />
          <Route path="/ai-image" element={<AiImageStudioPage />} />
          <Route path="/brands" element={<BrandManagerPage />} />
          <Route path="/director" element={<MarketingDirectorPage />} />
          <Route path="/scheduler" element={<MarketingCalendarPage />} />
          <Route path="/monthly" element={<MonthlyPlannerPage />} />
          <Route path="/plan" element={<PlanPage />} />
          <Route path="/checklist" element={<ChecklistPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/email" element={<EmailPage />} />
          <Route path="/seo" element={<SeoPage />} />
          <Route path="/promotions" element={<PromotionsPage />} />
          <Route path="/conversion" element={<ConversionOptimizer />} />
          <Route path="/competitors" element={<CompetitorsPage />} />
          <Route path="/ad-budget" element={<AdBudgetPage />} />
          <Route path="/roas" element={<RoasPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/shopify" element={<ShopifyPage />} />
          <Route path="/product-intel" element={<ProductIntelligencePage />} />
          <Route path="/launch" element={<LaunchChecklistPage />} />
          <Route path="/connections" element={<ConnectionsHealthPage />} />
          <Route path="/readiness" element={<SystemReadinessPage />} />
          {user.role === "super_admin" && <Route path="/deploy-validation" element={<DeploymentValidationPage />} />}
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/social" element={<AiSocialManagerPage />} />
          <Route path="/video-studio" element={<AiVideoStudioPage />} />
          <Route path="/campaign-history" element={<CampaignHistoryPage />} />
          <Route path="/comms" element={<AiCommsManagerPage />} />
          <Route path="/revenue" element={<AiRevenueManagerPage />} />
          <Route path="/employees" element={<AiEmployeesPage />} />
          <Route path="/sales-manager" element={<AiSpecialistPage specialistKey="sales" />} />
          <Route path="/seo-manager" element={<AiSpecialistPage specialistKey="seo" />} />
          <Route path="/ad-manager" element={<AiSpecialistPage specialistKey="advertising" />} />
          <Route path="/website-optimizer" element={<AiSpecialistPage specialistKey="website" />} />
          <Route path="/brand-assets" element={<BrandAssetsPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/usage" element={<BillingUsagePage />} />
          <Route path="/linkedin" element={<LinkedInCenterPage />} />
          <Route path="/media" element={<MediaLibraryPage />} />
          <Route path="/strategy" element={<ContentStrategyPage />} />
          <Route path="/leads" element={<LeadsPage />} />
          <Route path="/business-audit" element={<BusinessAuditPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          {user.role === "super_admin" && <Route path="/usage-control" element={<UsageControlPage />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ErrorBoundary>
      </AppLayout>
    </ClientProvider>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const done = useRef(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams((window.location.hash || "").replace(/^#/, "")).get("session_id");
    if (!sid) { navigate("/login", { replace: true }); return; }
    (async () => {
      try {
        await googleSession(sid);
        window.history.replaceState(null, "", window.location.pathname); // strip session_id from URL
        await refreshUser();
        navigate("/", { replace: true });
      } catch (e) {
        setError(e?.response?.data?.detail || "Google sign-in failed. Please try again.");
        setTimeout(() => navigate("/login", { replace: true }), 1800);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="min-h-screen bg-[#0D0D14] flex items-center justify-center text-[#9a9a9a] ai-thinking" data-testid="google-auth-callback">
      {error ? <span className="text-red-400">{error}</span> : "Signing you in with Google…"}
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster theme="dark" position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

function AppRoutes() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Handle the OAuth return (session_id in the URL fragment) BEFORE normal routing.
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/data-deletion" element={<DataDeletionPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/ai-team" element={<AiTeamPage />} />
      <Route path="/industries" element={<IndustriesPage />} />
      <Route path="/pricing" element={<PublicPricingPage />} />
      <Route path="/stories" element={<StoriesPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/drezoro" element={dz(<DrezoroHome />)} />
      <Route path="/drezoro/about" element={dz(<DrezoroAbout />)} />
      <Route path="/drezoro/companies" element={dz(<DrezoroCompanies />)} />
      <Route path="/drezoro/technology" element={dz(<DrezoroTechnology />)} />
      <Route path="/drezoro/news" element={dz(<DrezoroNews />)} />
      <Route path="/drezoro/contact" element={dz(<DrezoroContact />)} />
      <Route path="/drezoro/privacy" element={dz(<DrezoroLegal kind="privacy" />)} />
      <Route path="/drezoro/terms" element={dz(<DrezoroLegal kind="terms" />)} />
      <Route path="/*" element={<AuthGate />} />
    </Routes>
  );
}

export default App;
